import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, Switch, DatePicker, Radio, Alert, Row, Col } from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CBModal from './CBModal';
import axiosInstance from '../variables/api/axiosInstance';
import { TYPES_DOC_GED_MAP } from '../variables/constants';
import { partieGarantie, adresseUtilisable } from '../services/partieGarantie';
import { echangesPartenaires, NATURES_ENVOI } from '../services/echangesPartenaires';
import { tDocument, useLangueDocument } from '../i18n/documents';

const GREEN = '#1a6b3c';
const { TextArea } = Input;

/* Une réponse est attendue pour ce qui appelle une action du partenaire. */
const REPONSE_ATTENDUE_PAR_DEFAUT = ['DEMANDE_PIECES', 'OFFRE_INDEMNISATION', 'BAP', 'RECOURS', 'RELANCE'];

/**
 * Envoyer quelque chose d'un dossier à un partenaire, en disant à QUI.
 *
 * Le partenaire proposé est celui du dossier - bureau homologue pour un ET, compagnie
 * pour un TE - et l'autre reste choisissable (un TE écrit aussi à l'homologue). La
 * réponse attendue et son échéance font figurer l'envoi dans l'état « en attente de… ».
 *
 * `preset` : { nature, objet, corps, documents: [ossanGedDocumentTrackingId] } pour
 * ouvrir la fenêtre pré-remplie (transmission d'un PV, par exemple).
 *
 * Langues : la fenêtre suit celle de l'agent ; l'objet et le corps, qui partent chez
 * le partenaire, suivent celle du partenaire choisi. `preset.modele` = { objet, corps,
 * valeurs } désigne des textes de `documents:envoiPartenaire.modeles` à rédiger dans
 * cette langue ; un `preset.objet` / `preset.corps` en clair est repris tel quel.
 */
export default function EnvoiPartenaireModal({ open, onClose, sinistre, preset = null, onEnvoye }) {
  const { t } = useTranslation('documents');
  const [form] = Form.useForm();
  const [documents, setDocuments] = useState([]);
  const [adresses, setAdresses] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const role = Form.useWatch('role', form);
  const nature = Form.useWatch('nature', form);
  const parCourriel = Form.useWatch('envoyerParMail', form);
  const reponseAttendue = Form.useWatch('reponseAttendue', form);

  const partenaires = useMemo(() => [
    { role: 'COMPAGNIE', libelle: sinistre?.organismeMembreRaisonSociale, id: sinistre?.organismeMembreTrackingId },
    { role: 'BUREAU_HOMOLOGUE', libelle: sinistre?.organismeHomologueRaisonSociale, id: sinistre?.organismeHomologueTrackingId },
  ].filter((p) => p.id), [sinistre]);

  const choisi = partenaires.find((p) => p.role === role);
  const adresse = choisi ? adresses[choisi.id] : undefined;
  const adresseOk = adresseUtilisable(adresse);

  const { langue } = useLangueDocument(choisi?.id);
  const tDoc = tDocument(langue);
  const objetParDefaut = (n) => {
    if (preset?.modele?.objet) return tDoc(`envoiPartenaire.modeles.${preset.modele.objet}`, preset.modele.valeurs);
    if (preset?.objet) return preset.objet;
    return tDoc('envoiPartenaire.objet', {
      nature: tDoc(`envoiPartenaire.natures.${n}`),
      numero: sinistre?.numeroSinistreLocal || '',
    });
  };
  const corpsParDefaut = () => (preset?.modele?.corps
    ? tDoc(`envoiPartenaire.modeles.${preset.modele.corps}`, preset.modele.valeurs)
    : (preset?.corps || ''));

  useEffect(() => {
    if (!open || !sinistre) return;
    const n = preset?.nature || 'TRANSMISSION_PV';
    form.resetFields();
    form.setFieldsValue({
      role: partenaires.some((p) => p.role === partieGarantie(sinistre).role) ? partieGarantie(sinistre).role : partenaires[0]?.role,
      nature: n,
      objet: objetParDefaut(n),
      corps: corpsParDefaut(),
      envoyerParMail: true,
      reponseAttendue: REPONSE_ATTENDUE_PAR_DEFAUT.includes(n),
      echeanceReponse: dayjs().add(15, 'day'),
      documentsGed: preset?.documents || [],
    });
    axiosInstance.get(`/v1/ged/sinistres/${sinistre.sinistreTrackingId}/documents`, { skipErrorToast: true })
      .then((r) => {
        const docs = r.data?.data || [];
        setDocuments(docs);
        // Pièce désignée par son identifiant OssanGED (le PV, par exemple).
        const ids = preset?.documentsGedIds || [];
        if (ids.length) {
          form.setFieldValue('documentsGed', docs.filter((d) => ids.includes(d.ossanGedDocumentId)).map((d) => d.ossanGedDocumentTrackingId));
        }
      })
      .catch(() => setDocuments([]));
    partenaires.forEach((p) => {
      axiosInstance.get(`/v1/organismes/${p.id}`, { skipErrorToast: true })
        .then((r) => setAdresses((a) => ({ ...a, [p.id]: r.data?.data?.email || '' })))
        .catch(() => setAdresses((a) => ({ ...a, [p.id]: '' })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sinistre?.sinistreTrackingId]);

  // Le partenaire choisi (et donc la langue) peut changer après l'ouverture : l'objet
  // et le corps proposés suivent, tant que l'agent ne les a pas modifiés lui-même.
  useEffect(() => {
    if (!open || !sinistre) return;
    const maj = {};
    if (!form.isFieldTouched('objet') && !preset?.objet) maj.objet = objetParDefaut(form.getFieldValue('nature'));
    if (!form.isFieldTouched('corps') && preset?.modele?.corps) maj.corps = corpsParDefaut();
    if (Object.keys(maj).length) form.setFieldsValue(maj);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, langue]);

  const changerNature = (n) => {
    form.setFieldsValue({
      objet: tDoc('envoiPartenaire.objet', {
        nature: tDoc(`envoiPartenaire.natures.${n}`),
        numero: sinistre?.numeroSinistreLocal || '',
      }),
      reponseAttendue: REPONSE_ATTENDUE_PAR_DEFAUT.includes(n),
    });
  };

  const valider = async () => {
    const v = await form.validateFields();
    setEnvoi(true);
    try {
      const res = await echangesPartenaires.envoyer(sinistre.sinistreTrackingId, {
        nature: v.nature,
        organismeTrackingId: choisi?.id || null,
        objet: v.objet,
        corps: v.corps || null,
        canal: v.envoyerParMail ? 'MAIL' : 'COURRIER_POSTAL',
        envoyerParMail: !!v.envoyerParMail && adresseOk,
        reponseAttendue: !!v.reponseAttendue,
        echeanceReponse: v.reponseAttendue && v.echeanceReponse ? v.echeanceReponse.format('YYYY-MM-DD') : null,
        documentsGed: v.envoyerParMail ? (v.documentsGed || []) : [],
      });
      const message = res.data?.message || t('ecran.envoiPartenaire.envoiEnregistre');
      if (/NON envoyé/i.test(message)) toast.warning(message); else toast.success(message);
      onEnvoye?.(res.data?.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ecran.envoiPartenaire.envoiEchoue'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <CBModal
      title={t('ecran.envoiPartenaire.titre')}
      open={open}
      onCancel={onClose}
      onOk={valider}
      okText={parCourriel && adresseOk ? t('ecran.envoiPartenaire.envoyer') : t('ecran.envoiPartenaire.enregistrer')}
      cancelText={t('ecran.annuler')}
      confirmLoading={envoi}
      width={720}
      okButtonProps={{ disabled: !choisi, style: { background: GREEN, borderColor: GREEN } }}
    >
      {partenaires.length === 0 ? (
        <Alert type="warning" showIcon message={t('ecran.envoiPartenaire.aucunPartenaire')}
          description={t('ecran.envoiPartenaire.aucunPartenaireAide')} />
      ) : (
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="role" label={t('ecran.envoiPartenaire.destinataire')}>
            <Radio.Group>
              {partenaires.map((p) => (
                <Radio key={p.role} value={p.role}>
                  {p.role === 'COMPAGNIE' ? t('ecran.envoiPartenaire.compagnie') : t('ecran.envoiPartenaire.bureauHomologue')}{t('ecran.deuxPoints')} <strong>{p.libelle || '-'}</strong>
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={9}>
              <Form.Item name="nature" label={t('ecran.envoiPartenaire.nature')}>
                <Select options={NATURES_ENVOI} onChange={changerNature} />
              </Form.Item>
            </Col>
            <Col xs={24} md={15}>
              <Form.Item name="objet" label={t('ecran.envoiPartenaire.objet')} rules={[{ required: true, whitespace: true, message: t('ecran.envoiPartenaire.objetRequis') }]}>
                <Input maxLength={250} />
              </Form.Item>
            </Col>
          </Row>
          {langue === 'en' && (
            <Alert type="info" showIcon style={{ marginBottom: 12, borderRadius: 8 }} message={t('ecran.messageEnAnglais')} />
          )}
          <Form.Item name="corps" label={t('ecran.relance.message')}>
            <TextArea rows={4} maxLength={5000} placeholder={tDoc('envoiPartenaire.placeholderCorps')} />
          </Form.Item>
          <Row gutter={16} align="middle">
            <Col xs={24} md={12}>
              <Form.Item name="envoyerParMail" label={t('ecran.envoiPartenaire.parCourriel')} valuePropName="checked" style={{ marginBottom: 4 }}>
                <Switch disabled={!adresseOk} />
              </Form.Item>
              <div style={{ fontSize: 12, color: adresseOk ? '#6b7280' : '#b45309', marginBottom: 12 }}>
                {adresse === undefined ? t('ecran.envoiPartenaire.lectureAdresse')
                  : adresseOk ? adresse
                    : t('ecran.envoiPartenaire.pasAdresse', {
                      nom: choisi?.role === 'COMPAGNIE' ? t('ecran.envoiPartenaire.laCompagnie')
                        : choisi ? t('ecran.envoiPartenaire.leBureauHomologue') : t('ecran.envoiPartenaire.cePartenaire'),
                    })}
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="reponseAttendue" label={t('ecran.envoiPartenaire.reponseAttendue')} valuePropName="checked" style={{ marginBottom: 4 }}>
                <Switch />
              </Form.Item>
              {reponseAttendue && (
                <Form.Item name="echeanceReponse" style={{ marginBottom: 12 }}>
                  <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder={t('ecran.envoiPartenaire.echeanceReponse')} />
                </Form.Item>
              )}
            </Col>
          </Row>
          {parCourriel && adresseOk && (
            <Form.Item name="documentsGed" label={t('ecran.envoiPartenaire.piecesJointes')}>
              <Select
                mode="multiple" allowClear optionFilterProp="label" placeholder={t('ecran.envoiPartenaire.choisirPieces')}
                options={documents.map((d) => ({
                  value: d.ossanGedDocumentTrackingId,
                  label: `${d.titre} - ${TYPES_DOC_GED_MAP[d.typeDocument] || d.typeDocument}`,
                }))}
              />
            </Form.Item>
          )}
          {nature === 'TRANSMISSION_PV' && (
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {t('ecran.envoiPartenaire.aidePv')}
            </div>
          )}
        </Form>
      )}
    </CBModal>
  );
}
